import React, { useState, useEffect, useRef } from "react";
import "../../styles/audiobook/AudioBookCreationModal.scss";
import SelectMode from "./SelectMode";
import RecordingCountdown from "./RecordingCountdown";
import SelectAIVoice from "./SelectAIVoice";
import Recording from "./Recording";
import AudioLoadingModal from "./AudioLoadingModal";
import RecordingReview from "./RecordingReview";
import SaveRecordingModal from "./SaveRecordingModal";
import {
  confirmAuidoBookTitle,
  createAIAudioBook,
  createCustomVoiceAudiobook,
  getAudioBookList,
  saveTempRecordingFiles,
} from "../../api/LibraryAPI";
import CompleteMakeAudioBook from "./CompleteMakeAudioBook";
import {
  AudioRandomSentence,
  getRandomSentence,
} from "../../utils/AudioRandomSentence";

const AudioBookCreationModal = ({ bookData, userID, onClose }) => {
  const BOOK_ID = bookData.book_id;
  const USER_ID = localStorage.getItem("userID");

  const totalBookLength = bookData.contents_json.story.length;

  const [step, setStep] = useState(1);
  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedAI, setSelectedAI] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [recordings, setRecordings] = useState([]); // 파일 객체로 변환 전
  const [showSelectMode, setShowSelectMode] = useState(true);

  const [recordedFiles, setRecordedFiles] = useState([]); // blob 기반 file 담는 배열
  const [audioBookInfo, setAudioBookInfo] = useState(null);
  const [existingVoices, setExistingVoices] = useState([]);
  const [createdAudiobookId, setCreatedAudiobookId] = useState(null);

  const [probeText, setProbeText] = useState("");

  // 테스트
  const ALLOWED_EXT = ["mp3", "m4a", "wav", "webm", "ogg", "flac"];
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB

  const getAudioDuration = (file) =>
    // new Promise((resolve, reject) => {
    //     const url = URL.createObjectURL(file);
    //     console.log('url--->', url);
    //     const a = new Audio();
    //     a.preload = 'metadata';
    //     a.src = url;
    //     a.onloadedmetadata = () => {
    //         const d = a.duration;
    //         console.log('duration time', d);
    //         URL.revokeObjectURL(url);
    //         resolve(d);
    //     };
    //     a.onerror = () => {
    //         URL.revokeObjectURL(url);
    //         reject(new Error('duration-read-failed'));
    //     };
    // });
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const audio = new Audio();
      audio.preload = "metadata";

      const cleanup = () => URL.revokeObjectURL(url);

      audio.onloadedmetadata = () => {
        // Safari/일부 환경에서 duration이 Infinity/NaN일 때 보정
        if (!isFinite(audio.duration) || audio.duration === Infinity) {
          const onTimeUpdate = () => {
            audio.removeEventListener("timeupdate", onTimeUpdate);
            const d = audio.duration;
            console.log("duration time---->", d);
            cleanup();
            resolve(d);
          };
          audio.addEventListener("timeupdate", onTimeUpdate);
          // 아주 큰 값으로 점프 → duration 강제 계산
          audio.currentTime = 1e101;
          return;
        }
        const d = audio.duration;
        cleanup();
        resolve(d);
      };

      audio.onerror = () => {
        cleanup();
        reject(
          new Error(
            "이 기기/브라우저에서는 마이크 사용을 지원하지 않아요.\n" +
              "PC에서 다시 시도해주세요.",
          ),
        );
      };

      audio.src = url;
    });

  async function validateVoiceFile(file) {
    if (!file) throw new Error("voiceFile이 필요합니다");
    if (file.size === 0) throw new Error("빈 파일입니다");
    if (file.size > MAX_SIZE) throw new Error("파일 크기가 10MB를 초과합니다");

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      throw new Error(
        "지원하지 않는 파일 형식입니다 (mp3, m4a, wav, webm, ogg, flac만 가능)",
      );
    }

    const dur = await getAudioDuration(file); // 메타에서 읽힘?

    console.log("dur", dur);

    if (!isFinite(dur) || dur === 0) {
      console.log("오디오 길이를 확인할 수 없습니다. 파일을 다시 확인해주세요");
    }
    if (dur < 5 - 0.3)
      throw new Error("파일 길이가 너무 짧습니다 (5초 이상 필요)");
    if (dur > 60 + 0.3) throw new Error("파일 길이가 너무 깁니다 (최대 60초)");
  }
  // 테스트

  useEffect(() => {
    if (selectedMode === "myVoiceWithAI" && step === 3) {
      setProbeText(getRandomSentence());
    }
  }, [selectedMode, step]);

  const reshuffleProbeText = () => setProbeText(getRandomSentence());

  // 커스텀 오디오북
  const startCustomVoiceCreation = async (blob) => {
    if (!blob || blob.size === 0) {
      alert("녹음 파일이 비어있어요. 다시 시도해 주세요.");
      setStep(3);
      return;
    }

    const recordedType = blob.type || "audio/webm";
    const ext = recordedType.includes("mp4")
      ? "m4a"
      : recordedType.includes("ogg")
        ? "ogg"
        : recordedType.includes("mpeg")
          ? "mp3"
          : recordedType.includes("wav")
            ? "wav"
            : recordedType.includes("flac")
              ? "flac"
              : "webm";

    const voiceFile =
      blob instanceof File
        ? blob
        : new File([blob], `voice-sample.${ext}`, {
            type: recordedType,
            lastModified: Date.now(),
          });
    try {
      await validateVoiceFile(voiceFile);
    } catch (err) {
      alert(err?.message || "파일 검증 중 오류가 발생했어요.");
      setStep(3); // 다시 녹음 화면으로
      return; // 중요: 계속 진행 막기
    }

    // 검증 통과 후에 로딩 진입
    setStep(4);

    try {
      const res = await createCustomVoiceAudiobook({
        voiceFile,
        user_id: USER_ID,
        book_id: BOOK_ID,
      });

      if (res.success) {
        setAudioBookInfo(res.data);
        setCreatedAudiobookId(res.data?.audiobook_id);
        setStep(6); // 제목 입력 단계
      } else {
        if (res.errorCode === "VOLI_ERR_00") {
          alert(
            "현재 다른 사용자가 커스텀 보이스를 사용 중입니다. 잠시 후 다시 시도해 주세요.",
          );
        } else if (res.errorCode === "VOLI_ERR_01") {
          alert("AI가 잠시 바쁜가봐요! 잠시 후에 다시 시도해주세요!");
        } else if (res.errorCode === "RATE_LIMIT") {
          alert(
            res.errorMessage ||
              "현재 요청이 너무 많습니다. 약 1분 후 다시 시도해 주세요.",
          );
        } else if (res.errCode === "PARAM_ERR_00") {
          const msg = Array.isArray(res.errMsg)
            ? res.errMsg.map((e) => `${e.param}: ${e.msg}`).join("\n")
            : "요청 파라미터를 확인해주세요.";
          alert(msg);
        } else if (res.errorCode === "FILE_ERR_00") {
          console.log(res.errorMessage || "파일 유효성 검증에 실패했습니다.");
        } else {
          alert(
            res.errorMessage || "생성에 실패했어요. 잠시 후 다시 시도해주세요.",
          );
        }
        setStep(3);
      }
    } catch (e) {
      alert("네트워크 오류가 발생했어요. 잠시 후 다시 시도해주세요.");
      setStep(3);
    }
  };

  // AI기존 목소리 있는지 조회하기
  useEffect(() => {
    const fetchExistingAudioBooks = async () => {
      const res = await getAudioBookList(BOOK_ID);
      if (res.success) {
        const voices = res.data.audiobooks.map((ab) => ab.voice_type);
        setExistingVoices(voices);
      }
    };
    fetchExistingAudioBooks();
  }, []);

  const handleRecordingComplete = (blob) => {
    // 커스텀 오디오북 추가
    if (selectedMode === "myVoiceWithAI") {
      startCustomVoiceCreation(blob);
      return;
    }

    setRecordings((prev) => {
      const updated = [...prev];
      updated[currentPage] = blob;
      return updated;
    });

    if (currentPage + 1 < totalBookLength) {
      setCurrentPage((prev) => prev + 1);
    } else {
      setStep(4);
    }
  };

  // 녹음 오디오북 임시 저장
  const handleSaveTempRecordings = (blobs) => {
    const newFiles = blobs.map((blob, index) => {
      return new File([blob], `page${index + 1}.webm`, {
        type: "audio/webm",
      });
    });

    setRecordedFiles(newFiles);
    setStep(5);

    setTimeout(() => {
      saveTempRecordingFiles(USER_ID, BOOK_ID, newFiles)
        .then((res) => {
          console.log("임시저장된 오디오북 타이틀 결과값 여기로-->", res);
          if (res.success) {
            console.log("임시 저장 완료:", res.data);
            setCreatedAudiobookId(res.data.audiobook_id);
            setStep(6);
          } else {
            alert("임시 저장 실패!");
            setStep(4);
          }
        })
        .catch((err) => {
          console.error("저장 오류", err);
          alert("오류가 발생했어요.");
          setStep(4);
        });
    }, 100);
  };

  // AI목소리 선택
  const handleAIVoiceSelect = async (voiceType) => {
    setSelectedAI(voiceType);
    setStep(3);

    try {
      const res = await createAIAudioBook(USER_ID, BOOK_ID, voiceType);
      if (res.success) {
        setAudioBookInfo(res.data);
        setStep(7);
      } else {
        console.log(res.errorMessage || "생성실패");
        setStep(2);
      }
    } catch (err) {
      console.log("아예 실패 ㅠ", err);
      setStep(2);
    }
  };

  // 저장하기
  const handleFinalSave = async (title) => {
    if (!createdAudiobookId) {
      alert("오디오북 ID가 유효하지 않아요.");
      return;
    }

    setStep(5);

    try {
      const res = await confirmAuidoBookTitle(createdAudiobookId, title);
      if (res.success) {
        setAudioBookInfo(res.data);
        setStep(7);
      } else {
        alert(res.errorMessage || "오디오북 생성에 실패했어요.");
        setStep(6);
      }
    } catch (err) {
      console.error("에러:", err);
      alert("네트워크 오류 발생");
      setStep(6);
    }
  };

  console.log("selectedMode", selectedMode);

  const renderStep = () => {
    switch (step) {
      case 1:
        return showSelectMode ? (
          <SelectMode
            onSelect={setSelectedMode}
            onNext={() => setStep(2)}
            onClose={() => {
              setShowSelectMode(false);
              onClose();
            }}
          />
        ) : null;
      case 2:
        if (selectedMode === "voice" || selectedMode === "myVoiceWithAI") {
          return (
            <RecordingCountdown
              onDone={() => setStep(3)}
              onClose={onClose}
              selectedMode={selectedMode}
            />
          );
        }
        return (
          <SelectAIVoice
            bookId={BOOK_ID}
            onSelect={handleAIVoiceSelect}
            onClose={onClose}
            onPrevClick={() => {
              setSelectedAI(null);
              setStep(1);
            }}
            existingVoices={existingVoices}
          />
        );
      case 3:
        if (selectedMode === "ai") {
          return (
            <AudioLoadingModal text="AI 목소리로 오디오북을 생성 중이에요" />
          );
        }
        if (selectedMode === "voice") {
          return (
            <Recording
              page={currentPage}
              total={totalBookLength}
              sentence={bookData.contents_json.story[currentPage]?.page_content}
              onComplete={handleRecordingComplete}
              onConfirm={() => setStep(4)}
              onClose={onClose}
              selectedMode={selectedMode}
            />
          );
        }
        if (selectedMode === "myVoiceWithAI") {
          return (
            <Recording
              page={currentPage}
              total={totalBookLength}
              sentence={probeText}
              onComplete={(blob) => startCustomVoiceCreation(blob)}
              onConfirm={() => {}}
              onClose={onClose}
              selectedMode={selectedMode}
            />
          );
        }
        return null;
      case 4:
        if (selectedMode === "voice") {
          return (
            <RecordingReview
              recordings={recordings}
              sentences={bookData.contents_json.story.map(
                (p) => p.page_content,
              )}
              onComplete={() => {
                handleSaveTempRecordings(recordings);
              }}
            />
          );
        }
        return (
          <AudioLoadingModal
            text={
              selectedMode === "aiWithMyVoice"
                ? "내 목소리를 바탕으로 AI가 오디오북을 만들고 있어요"
                : "AI 목소리로 오디오북을 생성 중이에요"
            }
          />
        );
      case 5:
        if (selectedMode === "voice") {
          return (
            <AudioLoadingModal
              text="녹음한 목소리로 오디오북을 생성 중입니다"
              onDone={() => setStep(6)}
            />
          );
        }
        return null;
      case 6:
        if (selectedMode === "voice" || selectedMode === "myVoiceWithAI") {
          return (
            <SaveRecordingModal onClose={onClose} onSave={handleFinalSave} />
          );
        }
        return null;
      case 7:
        return (
          <CompleteMakeAudioBook
            audioBookInfo={audioBookInfo}
            onClose={onClose}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="audio-creation-modal">
      <div className="inner">{renderStep()}</div>
    </div>
  );
};

export default AudioBookCreationModal;
